/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { getTasks, updateTaskStatus } from '../services/api_service';
import {
  Typography,
  Container,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Box,
  Chip,
  IconButton,
  Collapse,
  Grid,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CallIcon from '@mui/icons-material/Call';
import SupportIcon from '@mui/icons-material/Support';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import DescriptionIcon from '@mui/icons-material/Description';
import { useAlert } from '../AlertContext';

export default function AdminTasks() {
  const [tasks, setTasks] = useState([]);
  const [expandedTask, setExpandedTask] = useState(null);
  const { showAlert } = useAlert();

  useEffect(() => {
    get_tasks();
  }, []);

  const get_tasks = async () => {
    getTasks()
      .then((response) => {
        if (response && response.result) {
          setTasks(response.result);
        }
      })
      .catch(() => {
        showAlert('Tuvimos un error procesando esta acción', 'error');
      });
  };

  const updateTask = async (id) => {
    const task = tasks.find((t) => t.id == id);
    if (task) {
      updateTaskStatus(task.id, { completed: !task.completed })
        .then(() => {
          showAlert('Tarea actualizada exitosamente', 'success');
          get_tasks();
        })
        .catch(() => {
          showAlert('Tuvimos un error procesando esta acción', 'error');
        });
    }
  };

  const toggleExpanded = (taskId) => {
    setExpandedTask(expandedTask === taskId ? null : taskId);
  };

  const getTaskTitle = (task) => {
    if (task.description.type === 'call') {
      return 'Llamar beneficiario para inscripción';
    }
    if (task.description.type === 'PQRS') {
      return 'Solucionar PQRSDF';
    }
    return 'Tarea pendiente';
  };

  const getTaskIcon = (task) => {
    if (task.description.type === 'call') {
      return <CallIcon sx={{ color: '#4caf50' }} />;
    }
    if (task.description.type === 'PQRS') {
      return <SupportIcon sx={{ color: '#ff9800' }} />;
    }
    return null;
  };

  const completedTasks = tasks.filter((t) => t.completed).length;
  const pendingTasks = tasks.filter((t) => !t.completed).length;

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 3 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Tareas Administrativas
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestiona y completa las tareas pendientes
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card sx={{ textAlign: 'center', p: 2, bgcolor: '#e3f2fd' }}>
            <Typography variant="h4" fontWeight="bold" color="primary">
              {pendingTasks}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Tareas Pendientes
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card sx={{ textAlign: 'center', p: 2, bgcolor: '#e8f5e8' }}>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {completedTasks}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Tareas Completadas
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {tasks.length === 0 ? (
        <Card sx={{ textAlign: 'center', p: 3 }}>
          <SupportIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No hay tareas pendientes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Todas las tareas están completadas
          </Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {tasks.map((task, index) => (
            <Card
              key={`task-${index}`}
              variant="outlined"
              sx={{
                transition: 'all 0.2s',
                '&:hover': { boxShadow: 2 },
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={task.completed}
                          onChange={() => updateTask(task.id)}
                          sx={{ mr: 1 }}
                        />
                      }
                      label=""
                      sx={{ mr: 2, minWidth: 'auto' }}
                    />

                    <Box
                      sx={{ display: 'flex', alignItems: 'center', flex: 1 }}
                    >
                      {getTaskIcon(task)}
                      <Box sx={{ ml: 1, flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            textDecoration: task.completed
                              ? 'line-through'
                              : 'none',
                            color: task.completed
                              ? 'text.secondary'
                              : 'text.primary',
                          }}
                        >
                          {getTaskTitle(task)}
                        </Typography>
                        <Chip
                          label={task.completed ? 'Completada' : 'Pendiente'}
                          color={task.completed ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </Box>

                  <IconButton onClick={() => toggleExpanded(task.id)}>
                    {expandedTask === task.id ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </Box>

                <Collapse in={expandedTask === task.id} timeout="auto">
                  <Divider sx={{ my: 2 }} />

                  {task.description.type === 'call' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                        >
                          <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Nombre del beneficiario
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="medium">
                          {task.description.beneficiary_name}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                        >
                          <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Teléfono del beneficiario
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="medium">
                          {task.description.beneficiary_phone}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                        >
                          <BadgeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Documento del titular
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="medium">
                          {task.description.user_document}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                        >
                          <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Nombre del titular
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="medium">
                          {task.description.user_name}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}

                  {task.description.type === 'PQRS' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                        >
                          <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Documento del usuario
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="medium">
                          {task.description.user_document}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                        >
                          <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Nombre del usuario
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="medium">
                          {task.description.user_name}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                        >
                          <SupportIcon
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Tipo
                          </Typography>
                        </Box>
                        <Chip
                          label={task.description.pqrs_type}
                          color="info"
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                        >
                          <DescriptionIcon
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Descripción
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          {task.description.description}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Container>
  );
}
